import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { selectCartPageAggregates } from "~/features/cart/cart/redux/selectors/cartPageAggregates";
import {
  selectDisableCheckout,
  selectIsPnaLoading,
  selectInvalidItems,
  type InvalidCartItem,
} from "~/features/cart/cart/redux/selectors/cartPageSelectors";
import { selectPackageEntryMap } from "~/features/cart/cart/redux/selectors/cartSelectors";
import {
  switchProductVersion,
  updatePriceReferenceForCart as updatePriceReferenceAction,
} from "~/features/cart/cart/redux/actions/cartPageActions";
import { deleteSectionSaga } from "~/features/cart/cart/redux/actions/cartQuoteCommonActions";
import { insertAlternateRow, closeAlternateRow } from "~/features/cart/cartV2/redux/cartAlternatesSlice";
import {
  selectOrderablesViewModel,
  selectBundleViewModels,
} from "~/features/cart/cartV2/redux/selectors/cartTableSelectors";
import { useSessionState } from "~/app/providers/SessionProvider";
import { activeCartResourceId } from "~/features/cart/cart/utils/cartResourceIdentity";
import { KAMELEOON_FLAGS, useKameleoonFlag } from "~/common/config/kameleoonFlags";

export type { InvalidCartItem };

export function useB2BCartPageViewModel(externalDisableCheckout: boolean) {
  const dispatch = useDispatch();
  const session = useSessionState();
  const activeCartId = activeCartResourceId(session.data?.activeCart, true);
  const campaignDiscountEnabled = useKameleoonFlag(KAMELEOON_FLAGS.campaigndiscount);

  const aggregatesSelector = useMemo(
    () => selectCartPageAggregates(campaignDiscountEnabled),
    [campaignDiscountEnabled],
  );
  const aggregates = useSelector(aggregatesSelector);
  const disableCheckout = useSelector(selectDisableCheckout(externalDisableCheckout));
  const isPnaLoading = useSelector(selectIsPnaLoading);
  const orderablesViewModel = useSelector(selectOrderablesViewModel);
  const bundleViewModels = useSelector(selectBundleViewModels);
  const invalidItems = useSelector(selectInvalidItems);
  const packageEntryMap = useSelector(selectPackageEntryMap);
  const packageEntryIds = Object.keys(packageEntryMap).map(Number);

  const orderablesTableId = orderablesViewModel.tableId;
  const orderablesProductGroupNumbers = orderablesViewModel.rows
    .filter((r): r is Extract<typeof r, { kind: "entry" }> => r.kind === "entry")
    .map((r) => r.productGroupNumber);

  const handleInsertAlternateRow = useCallback(
    (input: { anchorProductGroupNumber: number | null; productCode: string; designation: string }) => {
      dispatch(insertAlternateRow({ tableId: orderablesTableId, ...input }));
    },
    [dispatch, orderablesTableId],
  );

  const handleCloseAlternateRow = useCallback(
    (rowId: string) => {
      dispatch(
        closeAlternateRow({ tableId: orderablesTableId, rowId, productGroupNumbers: orderablesProductGroupNumbers }),
      );
    },
    [dispatch, orderablesTableId, orderablesProductGroupNumbers],
  );

  const handleDeleteSection = useCallback(
    (quoteNumber?: string) => {
      if (session.cartActionsDisabled) {
        return;
      }
      dispatch(deleteSectionSaga(quoteNumber ?? "", activeCartId));
    },
    [activeCartId, dispatch, session.cartActionsDisabled],
  );

  const handleUpdatePriceReferenceForCart = useCallback(
    (priceReference: string, onSuccess: (count: number) => void) => {
      if (session.cartActionsDisabled) {
        return;
      }
      dispatch(updatePriceReferenceAction(priceReference, packageEntryIds, onSuccess));
    },
    [dispatch, packageEntryIds, session.cartActionsDisabled],
  );

  const handleSwitchProductVersionForCart = useCallback(() => {
    if (session.cartActionsDisabled) {
      return;
    }
    dispatch(switchProductVersion());
  }, [dispatch, session.cartActionsDisabled]);

  return {
    aggregates,
    disableCheckout: disableCheckout || session.cartActionsDisabled,
    isPnaLoading,
    orderablesViewModel,
    bundleViewModels,
    invalidItems,
    handleInsertAlternateRow,
    handleCloseAlternateRow,
    handleDeleteSection,
    handleUpdatePriceReferenceForCart,
    handleSwitchProductVersionForCart,
  };
}
